import type {ReactNode} from 'react';
import React, {createContext, useCallback, useContext, useState} from 'react';

type QueryParams = Record<string, string | string[]>;

type QueryParamState<T extends QueryParams> = T;

export interface SetOptions {
  replaceParams?: boolean;
  stateful?: boolean;
}

const searchParamsToObj = (searchParams: URLSearchParams) =>
  Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
    if (!acc[key]) {
      return {
        ...acc,
        [key]: value,
      };
    }
    return {
      ...acc,
      [key]: [
        ...((Array.isArray(acc[key]) ? acc[key] : [acc[key]]) as string[]),
        value,
      ],
    };
  }, {} as QueryParams);

const useQueryParamContext = (): {
  queryParams: QueryParamState<QueryParams>;
  setQueryParams: (params: Partial<QueryParams>, options?: SetOptions) => void;
  urlSearchParams: URLSearchParams;
} => {
  const urlSearchParams = React.useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );

  const [queryParams, setQueryParams] = useState<QueryParamState<QueryParams>>(
    searchParamsToObj(urlSearchParams),
  );

  const setQueryParamsCb = useCallback(
    (
      params: Partial<QueryParams>,
      {replaceParams = true, stateful = true}: SetOptions = {},
    ) => {
      Object.entries(params).forEach(([key, value]) => {
        if (!value) {
          return urlSearchParams.delete(key);
        }
        const values = Array.isArray(value) ? value : [value];
        values.forEach((v, idx) => {
          if (replaceParams && idx === 0) {
            urlSearchParams.set(key, v);
          } else {
            urlSearchParams.append(key, v);
          }
        });
      });

      const replaceUrl = [window.location.pathname, urlSearchParams.toString()]
        .filter(Boolean)
        .join(`?`);

      window.history.replaceState(null, ``, replaceUrl);
      if (stateful) {
        setQueryParams((prevState) => ({
          ...prevState,
          ...searchParamsToObj(urlSearchParams),
        }));
      }
    },
    [urlSearchParams],
  );

  return {
    queryParams,
    setQueryParams: setQueryParamsCb,
    urlSearchParams,
  };
};

type QueryParamReturnType = ReturnType<typeof useQueryParamContext>;

export const createParamContext = <T extends unknown>() => {
  const context = createContext<T | undefined>(undefined);

  const useCtxt = () => {
    const ctx = useContext(context);
    if (!ctx) {
      throw new Error(
        `useQueryParams hook must be used within the QueryParamProvider`,
      );
    }
    return ctx;
  };

  return [useCtxt, context.Provider] as const;
};

const [useQueryParams, Provider] = createParamContext<QueryParamReturnType>();

const QueryParamProvider = ({children}: {children: ReactNode}) => {
  const {queryParams, setQueryParams, urlSearchParams} = useQueryParamContext();

  return (
    <Provider
      value={{
        queryParams,
        setQueryParams,
        urlSearchParams,
      }}
    >
      {children}
    </Provider>
  );
};

export {useQueryParams, QueryParamProvider};
